// Quiz embed relay. Independent contracts share this element and the one
// origin/source-checked message listener:
//
//   1. Frame sizing (quiz -> host, added 2026-08-06 for quiz T-421):
//        { type: 'roots:embed:height',     version: 1, height: <css px> }
//        { type: 'roots:embed:scroll-top', version: 1 }
//      The quiz reports its own content height and we apply it, which makes the
//      storefront page the ONLY scroller on the page. Before this, the section
//      pinned the frame to the viewport height below a 60 px header: the page
//      was always taller than the fold and always scrolled, while the quiz —
//      clamped to the frame by its own Chrome iOS scroll fix — could never grow
//      into what the page scrolled through, so the theme footer displaced it
//      (measured live: 63 px of an 844 px frame still visible at maximum
//      scroll). Sender is the quiz repo's src/lib/embed-height.ts. Both sides
//      clamp the height independently; the repos deploy on their own schedules.
//
//   2. Attribution (host -> quiz URL, added 2026-09-22). The section renders
//      the frame with no src; forwardAttribution sets it once to
//      quiz?embed=1 plus the page's own UTMs and click ids, because the quiz
//      reads attribution only from its own URL. A frame that already has a src
//      is left alone, so a reconnect never re-navigates it.
//
//   3. Consent (host -> quiz, added 2026-10-28; owner: "Option 2 it is"). The
//      quiz's Tracklution follows the visitor's Shopify consent:
//        quiz -> host  { type: 'roots:consent:request', version: 1 }
//        host -> quiz  { type: 'roots:consent', version: 1, allowed: <boolean> }
//      allowed = marketingAllowed() && saleOfDataAllowed(): false for an
//      undecided or declining visitor in a banner region, and after a US
//      opt-out or GPC (https://shopify.dev/docs/api/customer-privacy). We answer
//      the request and push again on visitorConsentCollected. If the API never
//      loads we send nothing and the quiz does not track. Receiver: the quiz
//      repo's src/lib/host-consent.ts. (The iubenda-era relays roots:privacy
//      and roots:purpose-consent are gone; a stray roots:privacy:ready is
//      ignored.)
//
// Loaded globally by snippets/scripts.liquid so a quiz section freshly added in
// the editor upgrades with no instance at page load.
// Regression: scripts/visual-regression/quiz-relay-test.mjs (the quiz-relay CI
// job) covers fresh editor adds, attribution, consent, frame height and
// listener cleanup.

// Call back with Shopify's Customer Privacy API once it is available, loading it
// if needed; never calls back if it does not load within 5 s.
function withCustomerPrivacy(callback, attempt = 0) {
  const cp = window.Shopify && window.Shopify.customerPrivacy;
  if (cp && typeof cp.marketingAllowed === 'function' && typeof cp.saleOfDataAllowed === 'function') {
    callback(cp);
    return;
  }
  if (!attempt && window.Shopify && window.Shopify.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
  }
  if (attempt < 50) setTimeout(() => withCustomerPrivacy(callback, attempt + 1), 100);
}

if (!customElements.get('roots-quiz-embed')) {
  customElements.define('roots-quiz-embed', class extends HTMLElement {
    connectedCallback() {
      this.initializeRelay();
    }

    disconnectedCallback() {
      this.teardownRelay();
    }

    initializeRelay() {
      this.teardownRelay();
      const iframe = this.querySelector('iframe');
      if (!iframe) return;

      this.iframe = iframe;
      this.forwardAttribution(iframe);
      this.controller = new AbortController();

      window.addEventListener('message', (event) => {
        if (event.origin !== 'https://quiz.rootspet.com') return;
        if (event.source !== iframe.contentWindow) return;
        const message = event.data;
        if (!message || typeof message !== 'object') return;
        if (message.type === 'roots:embed:height' && message.version === 1) {
          this.applyHeight(message.height);
        } else if (message.type === 'roots:embed:scroll-top' && message.version === 1) {
          this.scrollToTop();
        } else if (message.type === 'roots:consent:request' && message.version === 1) {
          this.sendConsent();
        }
      }, { signal: this.controller.signal });
      document.addEventListener('visitorConsentCollected', () => this.sendConsent(),
        { signal: this.controller.signal });
    }

    sendConsent() {
      const frame = this.iframe && this.iframe.contentWindow;
      if (!frame) return;
      withCustomerPrivacy((cp) => {
        if (!this.iframe || this.iframe.contentWindow !== frame) return;
        const allowed = Boolean(cp.marketingAllowed() && cp.saleOfDataAllowed());
        frame.postMessage({ type: 'roots:consent', version: 1, allowed }, 'https://quiz.rootspet.com');
      });
    }

    // The allowlist is the quiz's own ALLOWED_LANDING_PARAMS; the quiz clamps
    // lengths itself. Only a src-less frame is touched: setting the src once is
    // one navigation, where overwriting a static one would be two and would
    // double-count the quiz's landing step.
    // A staff / QA browser (the `roots_review_browser` marker, or the
    // `roots_internal` flag c-internal-consent-deny keeps) also passes the marker
    // on, so the quiz sends nothing to Tracklution or its funnel for it. A reset
    // (`=0`) is passed on too: the quiz keeps its own flag in its own origin.
    forwardAttribution(iframe) {
      if (iframe.hasAttribute('src')) return;
      const page = new URLSearchParams(window.location.search);
      const url = new URL('https://quiz.rootspet.com/quiz?embed=1');
      for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'ttclid', '_kx']) {
        const value = page.get(key);
        if (value) url.searchParams.set(key, value);
      }
      let marker = page.get('roots_review_browser') === '1' ? '1' : null;
      try {
        if (!marker && localStorage.getItem('roots_internal') === '1') marker = '1';
      } catch (e) {}
      if (page.get('roots_review_browser') === '0') marker = '0';
      if (marker) url.searchParams.set('roots_review_browser', marker);
      iframe.src = url.toString();
    }

    teardownRelay() {
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.iframe = null;
    }

    // Size the frame to the quiz's reported content height. Anything outside a
    // sane range is DROPPED rather than clamped, so a drifted or buggy sender
    // leaves the last good height standing instead of stretching the page. The
    // section's min-height stays the floor for short screens and for the case
    // where these messages never arrive at all.
    applyHeight(height) {
      if (!this.iframe) return;
      if (typeof height !== 'number' || !Number.isFinite(height)) return;
      if (height <= 0 || height > 20000) return;
      this.iframe.style.height = Math.ceil(height) + 'px';
    }

    // A new quiz screen opens at the top. Inside the frame that used to come
    // free from a scroller reset; now that the host owns the scroller, the
    // quiz has to ask. scroll-margin-top on the element keeps a sticky header
    // (a header-section setting, not a constant) off the top of the quiz.
    scrollToTop() {
      this.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  });
}
